import { useState } from "react";
import { toast } from "@/lib/toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useAddPapers } from "@/hooks/usePapers";
import { sourceLabel, truncateAuthors } from "@/lib/discovery-labels";
import type { ConceptSearchResponse, ConceptSearchResult } from "@/services/concept-search";

interface Props {
  conceptId: string;
  query: string;
  response: ConceptSearchResponse;
  onClose: () => void;
}

const IN_CONCEPT_LABEL: Record<string, string> = {
  active: "Already in this concept",
  dismissed: "Dismissed earlier",
};

/** Preview of Curate search results (OpenAlex slice 1b). Nothing is ticked:
 * every add is a deliberate pick — which is also the "do users pick
 * OpenAlex-found papers" signal (logged server-side by source). */
export function SearchResultsDialog({ conceptId, query, response, onClose }: Props) {
  const addPapers = useAddPapers(conceptId);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const { results, oa_only_count, openalex_status, query_used } = response;
  const selectable = results.filter((r) => r.in_concept === "none");
  const alreadyIn = results.length - selectable.length;
  const allSelected = selectable.length > 0 && selected.size === selectable.length;

  const toggleOne = (paperId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(paperId)) next.delete(paperId);
      else next.add(paperId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.paper_id)));
  };

  const handleAdd = () => {
    const chosen = selectable.filter((r) => selected.has(r.paper_id));
    if (chosen.length === 0) return;
    addPapers.mutate(
      {
        papers: chosen.map((r) => ({
          paper_id: r.paper_id,
          title: r.title,
          authors: r.authors,
          year: r.year ?? undefined,
          abstract: r.abstract ?? undefined,
          doi: r.doi ?? undefined,
          citations: r.citation_count,
          discovery_sources: r.sources.length ? r.sources : ["s2"],
        })),
        role: "trawled",
        added_source: "search",
      },
      {
        onSuccess: () => {
          toast.success(`Added ${chosen.length} paper${chosen.length === 1 ? "" : "s"}`);
          onClose();
        },
      },
    );
  };

  const sourcesLine =
    openalex_status === "off"
      ? "Searched Semantic Scholar"
      : openalex_status === "unavailable"
        ? "Searched Semantic Scholar (OpenAlex unavailable)"
        : "Searched Semantic Scholar and OpenAlex";
  // A per-row source label only means something when OpenAlex contributed
  // (SeedDiscoveryDialog's showSources rule).
  const showSources = results.some((r) => r.sources.includes("openalex"));
  const rescueQueries = response.rescue_queries ?? [];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Results for “{query}”</DialogTitle>
          <p className="text-sm text-muted-foreground" data-testid="search-results-subline">
            {sourcesLine} · {results.length} result{results.length === 1 ? "" : "s"}
            {alreadyIn > 0 && ` · ${alreadyIn} already in this concept`}
          </p>
          {rescueQueries.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="search-rescue-note">
              Few matches for “{query}” — also searched{" "}
              {rescueQueries.map((v) => `“${v}”`).join(" and ")}.
            </p>
          )}
          {query_used !== query && (
            <p className="text-xs text-muted-foreground">
              No results for the full phrase — showing results for “{query_used}”.
            </p>
          )}
        </DialogHeader>

        <div className="overflow-y-auto -mx-2 px-2 min-h-0">
          {selectable.length > 0 && (
            <button
              onClick={toggleAll}
              className="mb-2 text-xs text-muted-foreground hover:text-foreground transition-colors px-1"
            >
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          )}

          {results.map((r: ConceptSearchResult) => {
            const inConcept = r.in_concept !== "none";
            return (
              <label
                key={r.paper_id}
                data-testid={`search-row-${r.paper_id}`}
                className={
                  inConcept
                    ? "flex items-start gap-3 rounded-md px-3 py-2 opacity-50"
                    : "flex items-start gap-3 rounded-md px-3 py-2 hover:bg-muted/50 cursor-pointer transition-colors"
                }
              >
                <Checkbox
                  checked={inConcept || selected.has(r.paper_id)}
                  disabled={inConcept}
                  onCheckedChange={() => toggleOne(r.paper_id)}
                  className="mt-1"
                  aria-label={r.title}
                />
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium leading-snug">{r.title}</span>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {truncateAuthors(r.authors)}
                    {r.year ? `, ${r.year}` : ""}
                    {showSources && (
                      <>
                        {" · "}
                        <span data-testid={`search-source-${r.paper_id}`}>{sourceLabel(r.sources)}</span>
                      </>
                    )}
                    {inConcept && ` · ${IN_CONCEPT_LABEL[r.in_concept]}`}
                  </div>
                  {r.citation_count > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 mt-1">
                      {r.citation_count.toLocaleString()} citations
                    </Badge>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {oa_only_count > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="search-oa-only">
            {oa_only_count} more found only on OpenAlex (not yet addable)
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selected.size === 0 || addPapers.isPending}
            className="min-w-[9rem] whitespace-nowrap"
          >
            {addPapers.isPending
              ? "Adding..."
              : `Add ${selected.size} paper${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
